import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { openSecondScreenStream } from "@/utils/secondScreenStream";
import { createCommandSender } from "./secondScreenCommands";

/**
 * The contract the Audience Layer remote expects from the phone's own
 * surface.
 *
 * Declared here rather than imported from useAudienceLayerHost: the remote is
 * resolved at runtime by Module Federation, so there is no compile-time
 * module to share a type with (the desktop's own copy notes the same thing),
 * and that copy pins `surface` to the literal "panel", which this surface is
 * not.
 */
export interface SecondScreenHostContext {
  gameId: string;
  tenant: string;
  user: {
    id: string;
    name: string;
    avatarUrl?: string;
    jwt: string;
  };
  /**
   * Free second-screen analytics: `device` is already `NOT NULL DEFAULT
   * 'web'` on every context-bearing table in the API and already travels as
   * the `x-htp-device` header on every widget request, so setting it here
   * makes every message, reaction and feed event from a phone
   * distinguishable in the data with no schema change.
   */
  device: "mobile-web";
  apiBaseUrl?: string;
  surface: "second-screen";
  /**
   * Whether the game on the big screen is live, when it has said.
   *
   * Absent until the first playback command comes back, because a phone has no
   * way to know on its own. See the state that backs it.
   */
  isLive?: boolean;
  /**
   * Still undefined, and still deliberately so.
   *
   * A HostPlayer requires eleven methods a phone has no honest answer for:
   * getPositionMs, isPlaying, isStalled, seekMs, the rate pair and the volume
   * trio. Stubbing them would put a getPositionMs returning 0 back into the
   * codebase, which is the exact false reading getPlaybackPositionMs returns
   * null to avoid, and would tell the watch party code it has a player to
   * sync. What this surface CAN do lives in remotePlayer below.
   */
  player: undefined;
  /**
   * Playback this surface can drive without owning it.
   *
   * The three reel controls and nothing else, all of them commands sent to the
   * paired desktop rather than calls on a video element here. The Moments
   * panel takes `player ?? remotePlayer`, so it starts a reel the same way on
   * both surfaces and only the implementation differs.
   */
  remotePlayer: {
    playReel(url: string, offsetMs: number): Promise<boolean>;
    /**
     * A fan's own clip: an offset into the game, not a reel of its own, and
     * into ONE of the game's two broadcasts.
     *
     * The feed travels because this phone cannot see the video and so cannot
     * know which broadcast the big screen is on. The clip knows, which is the
     * only reliable answer either device has.
     */
    playClip(startMs: number, feed?: "home" | "away"): Promise<boolean>;
    restoreGame(): Promise<void>;
    backToLive(): Promise<{ ok: boolean; code: string; isLive: boolean }>;
  };
  /**
   * Ask the paired big screen to clip what it is playing right now.
   *
   * The position never travels. The desktop reads its own player in the same
   * instant a press of the buttons under its video would, which is the only
   * way the clip lands on the play the fan reacted to, and the only way it
   * gets a thumbnail at all: that comes from the decoded frame, which cannot
   * be sent here and back.
   *
   * Resolves false when the big screen could not be reached or declined.
   */
  requestClip(type: string): Promise<boolean>;
  /**
   * Honest absence, not 0. A phone has no player to ask, and 0 would be
   * stored as "at puck drop", a false reading where "unknown" is the truth.
   */
  getPlaybackPositionMs(): null;
  getGameClock(): null;
  /**
   * True, and the tenant decides for real.
   *
   * This was false, which left the download control on every Moment card
   * pressable but wired to the upgrade dialog: a fan on their phone holding an
   * Insider subscription was told to go and buy one. The plan was never the
   * reason.
   *
   * It cannot be computed here the way the Watch page computes it. That reads
   * `UserRole.Permissions` off the tenant access token (see
   * utils/audienceLayerEntitlement), and this route has no tenant token at all,
   * only its own second-screen one.
   *
   * It does not need to. That check is a courtesy, not the gate, and the gate
   * is upstream and unchanged: HTPVTVBE's consumeDownloadCredit takes only
   * UserID from the token and re-reads the fan's role from its own database
   * (`Model.Users.findOne({ where: { UserID }, include: ["UserRole"] })`), so
   * a phone gets exactly the entitlement the desktop would. A fan without the
   * plan is refused there and MomentsPanel puts the tenant's own sentence,
   * "You are not insider plan subscriber", into this route's toast, which says
   * more than the generic dialog did.
   */
  canDownload: true;
  notify(message: string, kind?: "info" | "error"): void;
  /** Renews this route's own session and hands back the new token, or null when it cannot. */
  refreshAuth(): Promise<string | null>;
  /**
   * Still a no-op, and still load-bearing, but for a narrower reason than
   * before.
   *
   * useMoments subscribes to this to write a clip LOCALLY, reading
   * getPlaybackPositionMs, which on a phone is honestly null. That has not
   * changed, so this must still never fire: a phone that wrote its own clips
   * would anchor every one of them at puck drop.
   *
   * What has changed is that the phone is no longer mute. Its clip bar calls
   * requestClip above, which asks the paired desktop to run its own
   * createClip against its own player. The phone makes clips now; it just
   * does not make them here.
   */
  clipRequests: {
    subscribe(handler: (type: string) => void): () => void;
  };
}

/** What redeeming a PIN against the session hands back. */
export interface RedeemedSecondScreenSession {
  sessionId: string;
  token: string;
  gameId: string;
  tenant: string;
  user: {
    id: string;
    name: string;
    avatarUrl?: string | null;
  };
}

interface Params {
  session: RedeemedSecondScreenSession;
  apiBaseUrl?: string;
  /**
   * Told when this phone's session stops being valid while mounted: the fan
   * hit Disconnect on the bigger screen, the server's grace period lapsed
   * while this phone was away, or the token could not be renewed before it
   * expired. The caller is responsible for taking the page out of the
   * "ready" state; this hook only reports it.
   */
  onRevoked?: () => void;
}

/**
 * This route's token is scoped to 900 seconds (15 minutes). Refreshed here
 * well inside that window, comfortably before expiry: the Audience Layer
 * widget only calls `refreshAuth` reactively, after one of its own requests
 * has already come back 401 (htp-audience-layer's src/chat/api/http.ts).
 * A 401 on an already-expired token cannot be renewed, so left to the
 * widget alone the token expires, the reactive refresh 401s for the same
 * reason the request that triggered it did, and the session dies for good
 * about fifteen minutes after the phone connects, mid-game.
 */
const PROACTIVE_REFRESH_MS = 12 * 60 * 1000;

/**
 * Builds the HostContext for the phone's own full-screen Audience Layer
 * mount, and keeps it alive for as long as it is rendered:
 *
 * - Opens the same reconnecting stream Task 9 built for the desktop
 *   (src/utils/secondScreenStream.ts), which is this phone's liveness signal
 *   to the server. The phone needs the reconnect even more than the desktop
 *   does: the stream drops every time the screen locks, and the server's
 *   grace period only helps a fan who put their phone down if the stream
 *   comes back when they pick it up again.
 * - Supplies `refreshAuth`, which the layer calls when its own requests start
 *   401ing, by asking this route's own token endpoint for a fresh one.
 */
export function useSecondScreenHost({
  session,
  apiBaseUrl,
  onRevoked,
}: Params): SecondScreenHostContext {
  const [token, setToken] = useState(session.token);

  /*
   * Whether the game on the big screen is live, or null until it says.
   *
   * A phone cannot know this: it has no player and the pairing carries no
   * game metadata. It rides back on every playback result instead, which is
   * enough because the only thing that needs it is a control that appears
   * after a playback command. Null rather than a default, so the layer can
   * tell "not told yet" from "told, and it is not live".
   */
  const [isLive, setIsLive] = useState<boolean | null>(null);

  // Read inside the stream and refreshAuth callbacks without making the
  // token part of their dependency arrays; kept current on every render.
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const mountedRef = useRef(true);

  /*
   * This phone's end of the command channel.
   *
   * One per session, because it holds the promises for commands in flight:
   * rebuilding it would strand whatever the clip bar is awaiting. It reads the
   * token through the same getter the stream uses, so a rotation is picked up
   * on the next call rather than freezing at mount.
   */
  const sender = useMemo(
    () => createCommandSender({
      sessionId: session.sessionId,
      apiBaseUrl,
      getJwt: () => tokenRef.current,
    }),
    [session.sessionId, apiBaseUrl],
  );
  // The stream effect below reads this rather than closing over `sender`, so
  // it does not have to reopen the stream when a new session builds a new one.
  const senderRef = useRef(sender);
  senderRef.current = sender;
  useEffect(() => () => sender.dispose(), [sender]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refreshAuth = useCallback(async (): Promise<string | null> => {
    try {
      const res = await fetch(
        `${apiBaseUrl ?? ""}/second-screen/${session.sessionId}/token`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${tokenRef.current}` },
        },
      );
      if (!res.ok) return null;

      const body = (await res.json()) as { token?: string };
      if (!body.token) return null;

      if (mountedRef.current) setToken(body.token);
      return body.token;
    } catch {
      return null;
    }
  }, [apiBaseUrl, session.sessionId]);

  useEffect(() => {
    // Local to this one session (this effect's own invocation), not a ref:
    // resets cleanly if this hook ever ran for a second session.sessionId in
    // the same mount, rather than staying stuck true or false from whatever
    // the last session left behind.
    let ended = false;
    let refreshTimer: ReturnType<typeof setTimeout> | null = null;
    // Assigned below, once openSecondScreenStream returns it. endSession can
    // fire before that (a proactive refresh can fail before the stream's own
    // first connection settles), so this starts as a no-op rather than a
    // value endSession would have to wait on.
    let disposeStream: (() => void) | null = null;

    // Whatever ends this session first, a `revoked` SSE event, a terminal
    // stream status (which already stopped retrying on its own), or a
    // proactive refresh that could not renew the token (which has not):
    // stops the other two from also firing onRevoked, closes the stream so
    // a dead session does not keep reconnecting to the server forever, and
    // clears the refresh timer immediately rather than leaving one last
    // no-op attempt scheduled against a session already known to be dead.
    const endSession = () => {
      if (ended) return;
      ended = true;
      if (refreshTimer) {
        clearTimeout(refreshTimer);
        refreshTimer = null;
      }
      disposeStream?.();
      onRevoked?.();
    };

    const scheduleRefresh = () => {
      refreshTimer = setTimeout(async () => {
        if (!mountedRef.current || ended) return;
        const refreshed = await refreshAuth();
        if (!mountedRef.current || ended) return;

        if (refreshed) {
          scheduleRefresh();
        } else {
          // The token could not be renewed. Trying again in another twelve
          // minutes would just repeat the same failure against a token that
          // is never coming back, so this ends the session the same way a
          // terminal stream status does rather than looping forever.
          endSession();
        }
      }, PROACTIVE_REFRESH_MS);
    };
    scheduleRefresh();

    disposeStream = openSecondScreenStream({
      url: `${apiBaseUrl ?? ""}/second-screen/${session.sessionId}/stream`,
      // A getter, not the token itself: openSecondScreenStream calls this on
      // every connection attempt, including reconnects, so a token rotated
      // by refreshAuth above is picked up by the very next reconnect without
      // this effect ever needing to re-run. Passing `token` here directly
      // would freeze it at whatever value existed when the effect ran, which
      // is exactly the bug this replaced: the phone's stream drops every
      // time the screen locks, refreshAuth rotates the 15 minute token in
      // between, and the next reconnect used to go out with the token that
      // just expired.
      getJwt: () => tokenRef.current,
      /*
       * Belt to the proactive refresh above.
       *
       * That timer covers a phone that stays awake. A phone whose screen was
       * locked past the twelve minute mark wakes carrying a token that already
       * expired, reconnects with it, and gets a 401 the stream would otherwise
       * treat as the session ending. Renewing here turns that into a reconnect.
       */
      refreshAuth,
      onEvent: ({ event, data }) => {
        /*
         * The big screen's answer to something this phone asked for. Handed
         * to the sender, which is holding the promise the clip bar or the
         * Moments panel is awaiting.
         *
         * Both ends of a pairing see every event on this channel, so this
         * mount also receives `command` events (its own instructions, echoed)
         * and results for commands another tab issued. The sender ignores
         * what it is not waiting on; `command` falls through to nothing here,
         * which is correct: a phone does not execute commands, it sends them.
         */
        if (event === "command.result") {
          // The big screen stamps isLive onto every playback result. Read
          // before handing the result on, so the label is right by the time
          // whatever awaited this command renders.
          const live = (data as { detail?: { isLive?: unknown } } | undefined)?.detail?.isLive;
          if (typeof live === "boolean" && mountedRef.current) setIsLive(live);
          senderRef.current?.handleResult(data);
          return;
        }

        // Any other end revoking this session (the fan disconnecting from
        // the bigger screen, or the grace period lapsing) needs this phone
        // to stop showing the layer rather than sit on a dead mount with no
        // visible failure, the same trap Task 9 found on the desktop side.
        if (event === "revoked" && mountedRef.current) endSession();
      },
      // A 401 here means this session's token is expired or the session
      // itself was revoked server-side before this phone could reconnect to
      // hear the `revoked` event: same outcome as one, so it gets the same
      // handling rather than retrying a session that is never coming back.
      onTerminal: () => {
        if (mountedRef.current) endSession();
      },
    });

    return () => {
      ended = true;
      if (refreshTimer) clearTimeout(refreshTimer);
      disposeStream?.();
    };
    // Reopened only if the session itself changes (apiBaseUrl or
    // session.sessionId), which is exactly the pair refreshAuth's own
    // identity is tied to as well, so it is always read fresh here without
    // needing to be listed: this effect never runs on a stale refreshAuth.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.sessionId, apiBaseUrl]);

  return useMemo<SecondScreenHostContext>(
    () => ({
      gameId: session.gameId,
      tenant: session.tenant,
      user: {
        id: session.user.id,
        name: session.user.name,
        avatarUrl: session.user.avatarUrl ?? undefined,
        jwt: token,
      },
      device: "mobile-web",
      apiBaseUrl,
      surface: "second-screen",
      // Omitted entirely until the big screen has said, so the layer falls
      // back to its own assumption rather than being told a confident false.
      ...(isLive === null ? {} : { isLive }),
      player: undefined,
      getPlaybackPositionMs: () => null,
      getGameClock: () => null,
      // The tenant runs the real check per request; see the type above.
      canDownload: true,
      notify: (message: string, kind: "info" | "error" = "info") => {
        if (kind === "error") toast.error(message);
        else toast.info(message);
      },
      refreshAuth,
      clipRequests: { subscribe: () => () => {} },

      requestClip: async (type: string) => {
        const result = await sender.send({ type: "clip", payload: { clipType: type } });
        return result.ok;
      },

      remotePlayer: {
        /*
         * Only the PATH may travel.
         *
         * MomentsPanel builds an absolute url from its own apiBaseUrl, and the
         * desktop prepends its own, so what crosses the wire is root-relative
         * and the desktop can only ever load from the API it was already
         * configured with. A url from anywhere else is refused here rather
         * than sent, which is the client half of the rule the API also
         * enforces on manifestPath.
         */
        playReel: async (url: string, offsetMs: number) => {
          const base = apiBaseUrl ?? "";
          if (!base || !url.startsWith(base)) return false;
          const result = await sender.send({
            type: "play_reel",
            payload: { manifestPath: url.slice(base.length), offsetMs },
          });
          return result.ok;
        },

        /*
         * A fan's own clip, which is an offset into the game rather than a
         * reel. One command, because the restore, seek and play it needs are
         * only atomic on the device that owns the video.
         */
        playClip: async (startMs: number, feed?: "home" | "away") => {
          const result = await sender.send({
            // Defaulted rather than omitted: the API defaults a missing feed to
            // home anyway, and saying so here keeps the command readable in a
            // log next to the ones that name away.
            type: "play_clip", payload: { startMs, feed: feed ?? "home" },
          });
          return result.ok;
        },

        // MomentsPanel's existing "back to game" control calls this, and on
        // this surface it means the same thing as the one below.
        restoreGame: async () => { await sender.send({ type: "back_to_live" }); },

        backToLive: async () => {
          const result = await sender.send({ type: "back_to_live" });
          // send() answers in the transport's own shape; backToLive promises
          // isLive, which travels inside detail. This is the only place this
          // surface ever learns whether the game is live, which is enough:
          // the strip that needs it only appears after a command.
          return {
            ok: result.ok,
            code: result.code ?? "",
            isLive: result.detail?.isLive === true,
          };
        },
      },
    }),
    [
      session.gameId,
      session.tenant,
      session.user.id,
      session.user.name,
      session.user.avatarUrl,
      token,
      apiBaseUrl,
      refreshAuth,
      sender,
      isLive,
    ],
  );
}
